import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useSettings } from "../lib/SettingsContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { LogOut, User, ShieldCheck, Menu, X, MapPin, Plus } from "lucide-react";
import { db } from "../lib/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useCatalog } from "../lib/CatalogContext";
import { toast } from "sonner";
import { Spinner } from "./Spinner";

const STORE_LOCATOR_URL = "https://maps.app.goo.gl/T9syy6Qj8Jyna8QBA?g_st=iw";

export default function Header() {
  const { user, profile, logout } = useAuth();
  const { bookingEnabled } = useSettings();
  const { vehicleCategories } = useCatalog();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
  const [addingSave, setAddingSave] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate("/");
    } finally {
      setLoggingOut(false);
    }
  };

  const close = () => setMobileOpen(false);

  const openAddVehicle = () => {
    close();
    if (!user) { navigate("/login"); return; }
    setShowAddVehicle(true);
  };

  const handleAddVehicle = async () => {
    if (!addForm.name.trim()) { toast.error("Vehicle name is required"); return; }
    setAddingSave(true);
    try {
      await addDoc(collection(db, "cars"), {
        uid: user.uid,
        name: addForm.name.trim(),
        registration: addForm.registration.trim().toUpperCase(),
        category: addForm.category,
        createdAt: serverTimestamp(),
      });
      toast.success("Vehicle added!");
      setAddForm({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
      setShowAddVehicle(false);
    } catch (err) { toast.error(err.message); }
    finally { setAddingSave(false); }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#D4D4D8]"
      >
        <div className="max-w-7xl mx-auto px-6 py-0 flex items-center justify-between">
          <Link to="/" data-testid="logo-link">
            <img src="/logo.png" alt="Baywash" style={{ width: 140, height: "auto", objectFit: "contain" }} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <Link to="/" data-testid="nav-home" className="hover:text-[#002FA7] transition-colors">Home</Link>
            {bookingEnabled ? (
              <Link to="/book" data-testid="nav-book" className="hover:text-[#002FA7] transition-colors">Book a Wash</Link>
            ) : (
              <span data-testid="nav-book" className="text-[#D4D4D8] cursor-not-allowed">Book a Wash (Coming Soon)</span>
            )}
            <Link to="/subscriptions" data-testid="nav-subscriptions" className="hover:text-[#002FA7] transition-colors">Subscriptions</Link>
            <a href={STORE_LOCATOR_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#002FA7] transition-colors flex items-center gap-1">
              <MapPin size={14} /> Store Locator
            </a>
            {user && <Link to="/dashboard" data-testid="nav-dashboard" className="hover:text-[#002FA7] transition-colors">Dashboard</Link>}
            {user && (
              <button
                data-testid="nav-add-vehicle"
                onClick={openAddVehicle}
                className="flex items-center gap-1 hover:text-[#002FA7] transition-colors"
              >
                <Plus size={14} /> Add Vehicle
              </button>
            )}
            {user && <Link to="/profile" data-testid="nav-profile" className="hover:text-[#002FA7] transition-colors">Profile</Link>}
            {profile?.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1 text-[#002FA7]">
                <ShieldCheck size={16} /> Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {/* Desktop auth */}
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  <span className="flex items-center gap-2 text-sm">
                    <User size={16} /> {profile?.name || user.phoneNumber}
                  </span>
                  <Button
                    data-testid="logout-button"
                    onClick={handleLogout}
                    disabled={loggingOut}
                    variant="outline"
                    className="rounded-none border-2 border-black hover:bg-black hover:text-white font-bold uppercase tracking-wide text-xs"
                  >
                    {loggingOut ? <Spinner /> : <><LogOut size={14} className="mr-1" /> Logout</>}
                  </Button>
                </>
              ) : (
                <Link to="/login" data-testid="header-login-link">
                  <Button className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden p-1" aria-label="Open menu">
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 rounded-none border-l border-[#D4D4D8] p-0 [&>button]:hidden">
                <div className="flex flex-col h-full">
                  {/* Mobile header */}
                  <div className="flex items-center justify-between px-6 py-5 border-b border-[#D4D4D8]">
                    <Link to="/" onClick={close}>
                      <img src="/logo.png" alt="Baywash" style={{ width: 80, height: "auto", objectFit: "contain" }} />
                    </Link>
                    <button onClick={close} aria-label="Close menu">
                      <X size={20} />
                    </button>
                  </div>

                  {/* Mobile nav links */}
                  <nav className="flex flex-col flex-1 px-6 pt-4 overflow-y-auto">
                    {[
                      { to: "/", label: "Home" },
                      { to: "/book", label: bookingEnabled ? "Book a Wash" : "Book a Wash (Coming Soon)", disabled: !bookingEnabled },
                      { to: "/subscriptions", label: "Subscriptions" },
                      ...(user ? [{ to: "/dashboard", label: "Dashboard" }] : []),
                      ...(user ? [{ to: "/profile", label: "Profile" }] : []),
                    ].map((l) => (
                      l.disabled ? (
                        <span
                          key={l.to}
                          className="font-mono-accent text-xs py-4 border-b border-[#F4F4F5] text-[#D4D4D8] cursor-not-allowed"
                        >
                          {l.label.toUpperCase()}
                        </span>
                      ) : (
                        <Link
                          key={l.to}
                          to={l.to}
                          onClick={close}
                          className="font-mono-accent text-xs py-4 border-b border-[#F4F4F5] hover:text-[#002FA7] transition-colors"
                        >
                          {l.label.toUpperCase()}
                        </Link>
                      )
                    ))}
                    {user && (
                      <button
                        onClick={openAddVehicle}
                        className="font-mono-accent text-xs py-4 border-b border-[#F4F4F5] hover:text-[#002FA7] transition-colors text-left flex items-center gap-2"
                      >
                        <Plus size={12} /> ADD VEHICLE
                      </button>
                    )}
                    <a
                      href={STORE_LOCATOR_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={close}
                      className="font-mono-accent text-xs py-4 border-b border-[#F4F4F5] hover:text-[#002FA7] transition-colors flex items-center gap-2"
                    >
                      <MapPin size={12} /> STORE LOCATOR
                    </a>
                    {profile?.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={close}
                        className="font-mono-accent text-xs py-4 border-b border-[#F4F4F5] text-[#002FA7] flex items-center gap-2"
                      >
                        <ShieldCheck size={12} /> ADMIN
                      </Link>
                    )}
                  </nav>

                  {/* Mobile auth */}
                  <div className="px-6 py-6 border-t border-[#D4D4D8]">
                    {user ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-[#525252] font-mono-accent text-xs">
                          <User size={14} /> {profile?.name || user.phoneNumber}
                        </div>
                        <Button
                          onClick={() => { handleLogout(); close(); }}
                          disabled={loggingOut}
                          variant="outline"
                          className="w-full rounded-none border-2 border-black hover:bg-black hover:text-white font-bold uppercase tracking-wide text-xs"
                        >
                          {loggingOut ? <Spinner /> : <><LogOut size={14} className="mr-1" /> Logout</>}
                        </Button>
                      </div>
                    ) : (
                      <Link to="/login" onClick={close}>
                        <Button className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs">
                          Sign In
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Add Vehicle Dialog */}
      <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
        <DialogContent className="max-w-md rounded-none border-[#D4D4D8]">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tighter">Add Vehicle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">VEHICLE NAME</label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g. My Swift, Dad's SUV…"
                className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
              />
            </div>
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">CATEGORY</label>
              <Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}>
                <SelectTrigger className="rounded-none border-[#D4D4D8]">
                  <SelectValue placeholder="Select type…" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {vehicleCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">REGISTRATION (OPTIONAL)</label>
              <Input
                value={addForm.registration}
                onChange={(e) => setAddForm({ ...addForm, registration: e.target.value.toUpperCase() })}
                placeholder="KA-01-AB-1234"
                style={{ textTransform: "uppercase" }}
                className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12 font-mono"
              />
            </div>
            <Button
              onClick={handleAddVehicle}
              disabled={addingSave}
              className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12"
            >
              {addingSave ? <Spinner /> : "Save Vehicle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

